<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="navigation">
					<app-heading :title="`${website.name} Navigation`">
						<template v-slot:left>
							<app-button v-if="$ravello.can('website.post')"
								class="d-none d-sm-inline-flex"
								color="success"
								small
								outlined
								:to="{ name: 'website-navitem', params: { item: 'new' } }"
							>Add Item</app-button>
						</template>
						<template v-slot:right>
							<app-button v-if="$ravello.can('website.post')"
								color="secondary"
								small
								outlined
								@click="onPublish"
							>Publish</app-button>
						</template>
					</app-heading>
					<app-row>
						<app-cell>
							<input-button-group v-model="navigationView" color="primary" mandatory>
								<app-button no-margin small>Items</app-button>
								<app-button no-margin small>Main Navigation</app-button>
								<app-button no-margin small>Featured links</app-button>
							</input-button-group>
						</app-cell>
					</app-row>

					<!-- Navigation items -->
					<app-row v-if="navigationView === 0">
						<app-cell>
							<app-data-table
								:items="items"
								:headers="headers"
								:total="count"
								:refresh.sync="refresh"
								:sort-by="defaultTableSort"
								:initial-state="tableState"
								@update="fetchItems"
							>
								<template v-slot:quick_actions="{ item }">
									<app-tooltip text="Toggle featured in footer">
										<app-button
											:icon="item.featured ? 'fa-star' : 'far fa-star'"
											icon-only
											table
											icon-button
											:color="item.featured ? 'primary' : undefined"
											@click="toggleData(item, 'featured')"
										/>
									</app-tooltip>
									<app-tooltip text="Toggle visibility in main nav">
										<app-button
											:icon="item.hidden ? 'fa-eye-slash' : 'fa-eye'"
											icon-only
											table
											icon-button
											:color="item.hidden ? undefined : 'primary'"
											@click="toggleData(item, 'hidden')"
										/>
									</app-tooltip>
								</template>
								<template v-slot:published="{ item }">
									<app-icon
										v-if="item.published" icon="fa-check-circle"
										:color="item.updated ? 'warning' : 'success'"
									/>
									<app-icon v-else icon="fa-times-circle" color="error" />
								</template>
								<template v-slot:tags="{ item }">
									{{ formatTags(item.tags) }}
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										v-if="$ravello.can('website.put')"
										color="secondary"
										:to="{ name: 'website-navitem', params: { item: item._id } }"
										icon="fa-pencil-alt"
										table
									>
										Edit
									</app-button>
									<app-button-confirm
										v-if="$ravello.can('website.delete')"
										color="error"
										icon="fa-trash"
										table
										label-confirm="Remove"
										:description="`Are you sure you want to remove navigation item`
											+ ` <b>${item.slug}</b>?`"
										title="Remove Navigation Item"
										@click="onDelete(item._id)"
									>
										Remove
									</app-button-confirm>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>

					<!-- Navigation view -->
					<app-row v-if="navigationView === 1">
						<app-cell>
							<app-section>
								<v-treeview
									:items="navigation"
									:load-children="fetchNavigationItems"
									hoverable
									open-on-click
								>
									<template v-slot:append="{ item }">
										<app-button flat @click.stop="swapItem(navigation, item, -1)">
											<app-icon icon="fa-arrow-up" />
										</app-button>
										<app-button flat @click.stop="swapItem(navigation, item, 1)">
											<app-icon icon="fa-arrow-down" />
										</app-button>
									</template>
								</v-treeview>
							</app-section>
						</app-cell>
					</app-row>

					<!-- Featured links -->
					<app-row v-if="navigationView === 2">
						<app-cell>
							<app-section>
								<v-treeview
									:items="featured"
									:load-children="fetchFeaturedItems"
									hoverable
									open-on-click
								>
									<template v-slot:append="{ item }">
										<app-button flat @click.stop="swapItem(featured, item, -1, 'featured_position', true)">
											<app-icon icon="fa-arrow-up" />
										</app-button>
										<app-button flat @click.stop="swapItem(featured, item, 1, 'featured_position', true)">
											<app-icon icon="fa-arrow-down" />
										</app-button>
									</template>
								</v-treeview>
							</app-section>
						</app-cell>
					</app-row>
					<app-floating-button
						class="d-inline-flex d-sm-none"
						:to="{ name: 'website-navitem', params: { item: 'new' } }"
						color="success"
						icon="fa-plus"
					/>
				</app-tab-item>
			</template>
		</app-tabs>
	</app-page>
</template>

<script>
import { tableState } from '@/mixins'
import { websiteContentTabs } from '@/datasources/tabs'

const NAV_REQUEST_BATCH = 25

export default {
	mixins: [tableState],
	data() {
		return {
			website: {
				template: false,
			},
			items: [],
			navigation: [],
			featured: [],
			navigationView: 0,
			isPublishing: false,
			tabs: [],
			count: -1,
			refresh: false,
			children: {
				items: [],
				count: -1,
				refresh: false,
				parent: null,
			},
			headers: [{
				text: 'Quick actions',
				value: 'quick_actions',
				sortable: false,
			}, {
				text: 'Published?',
				value: 'published',
				sortable: true,
			}, {
				text: 'Parent',
				value: 'parent',
				sortable: true,
				cb: item => (item.data_parent ? item.data_parent.name : ''),
			}, {
				text: 'Name',
				value: 'name',
				sortable: true,
			}, {
				text: 'Slug (Display ID)',
				value: 'slug',
				sortable: true,
			}, {
				text: 'URL',
				value: 'url',
				sortable: true,
			}, {
				text: 'Page',
				value: 'page.name',
				sortable: false,
			}, {
				text: 'Root tags',
				value: 'tags',
				sortable: false,
				cb: item => (item.tags || []).join(', '),
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				width: '20%',
				sortable: false,
			}],
			defaultTableSort: 'name',
		}
	},
	computed: {
		websiteId() {
			return this.$route.params.website
		},
	},
	watch: {
		navigationView(value) {
			if (value === 1) {
				this.navigation = []
				this.fetchNavigationItems()
			} else if (value === 2) {
				this.featured = []
				this.fetchFeaturedItems()
			}
		},
	},
	async mounted() {
		const request = this.$ravello.graphql('website', this.websiteId)
			.fields(['name', 'slug'])

		this.website = await request.query()
		this.tabs = websiteContentTabs(this.websiteId)
	},
	methods: {
		formatTags(tags) {
			return tags.map((orTags) => {
				if (typeof orTags === 'string') { return orTags }
				return orTags.join('|')
			}).join(',')
		},
		async fetchItems(pagination) {
			this.updateTableState(pagination)

			const request = this.$ravello.graphql('navItems')
				.parent(this.$ravello.graphql('website', this.websiteId))
				.include(this.$ravello.graphql('page').field('name'))
				.include(this.$ravello.graphql('data_parent').field('name'))
				.fields(['_id', 'name', 'parent', 'slug', 'url', 'featured', 'hidden', 'published', 'updated', 'tags'])
				.sort('parent')
				.fromPagination(pagination)

			if (this.count === -1) {
				request.count('count')
			}

			try {
				this.items = await request.query()
				console.log(this.items)
				if (this.count === -1) {
					this.count = request.results.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async fetchNavigationItems(parent, featured = false) {
			const request = this.$ravello.graphql('navItems')
				.parent(this.$ravello.graphql('website', this.websiteId))
				.fields(['_id', 'name', 'ancestors', 'position', 'featured_position'])
				.limit(NAV_REQUEST_BATCH)
				.sort(featured ? 'featured_position' : 'position')

			if (featured) {
				request.filter('featured', true)
			} else {
				request
					.filter('or', [{ hidden: false }, { hidden: { exists: false } }])
					.field('childrenCount')

				if (parent) {
					request.filter('parent', parent.id)
				} else {
					request.filter('parent', { exists: false })
				}
			}

			let count = 0
			let offset = 0
			let items = []

			do {
				request.offset(offset)
				offset += NAV_REQUEST_BATCH

				// eslint-disable-next-line no-await-in-loop
				const response = await request.query()
				count = response.length
				items = [...items, ...response.map(item => ({
					id: item._id,
					name: item.name,
					ancestors: item.ancestors || [],
					position: item.position,
					featured_position: item.featured_position,
					...(item.childrenCount ? { children: [] } : {}),
				}))]
			} while (count === NAV_REQUEST_BATCH)

			if (parent) {
				parent.children = items
			} else if (featured) {
				this.featured = items
			} else {
				this.navigation = items
			}
		},

		async fetchFeaturedItems(parent) {
			this.fetchNavigationItems(parent, true)
		},

		async swapItem(root, item, mod, field = 'position', skipAncestors = false) {
			let parent = root

			if (!skipAncestors) {
				item.ancestors.forEach((ancestorId) => {
					parent = parent.find(parentItem => parentItem.id === ancestorId).children
				})
			}

			const index = parent.findIndex(parentItem => parentItem.id === item.id)
			const swapItem = parent[index + mod]
			if (!swapItem) { return }
			const currentPosition = item[field]
			item[field] = swapItem[field]
			swapItem[field] = currentPosition

			parent.splice(index, 1)
			parent.splice(index + mod, 0, item)

			await Promise.all([
				this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('navitems')
					.to(item.id)
					.update({
						[field]: item[field],
					}),
				this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('navitems')
					.to(swapItem.id)
					.update({
						[field]: swapItem[field],
					}),
			])
		},

		async toggleData(item, field, isChild = false) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('navitems')
					.to(item._id)
					.update({
						[field]: !item[field],
					})

				const items = isChild ? this.children.items : this.items
				const i = items.findIndex(el => el._id === item._id)
				items[i][field] = !item[field]
				items[i].updated = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async onDelete(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('navitems')
					.to(id)
					.delete()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async onPublish() {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('navitems')
					.to('build')
					.create()

				this.refresh = true
				this.$notifications.pushAlert('success', 'Navigation published!')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
			}
			this.$store.dispatch('app/stopLoading')
		},
	},
}
</script>
